
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Theme, Tooltip, useTheme } from "@mui/material";
import { GridColDef, GridColumnVisibilityModel } from "@mui/x-data-grid-pro";
import moment from "moment";
import { formatDate } from "utils/date-utils";
import useGridColumns, { ColumnVisibilityOrOrderChangeHandler } from "hooks/shared/useGridColumns";
import { IDashboardSettings } from "components/preferences";
import { DateFilterOperator, TaskProgressFilterOperator, TextFilterOperator, TaskStatusFilterOperator } from "components/shared/filters";
import { TaskProgressPill, TaskStatusCell } from "components/tasks";
import { TaskFilter } from "models/tasks";
import { SetPreferenceSignature } from "hooks/preferences";
import { useProjectPermissions } from "hooks/projects";

type UseWorkItemTasksColumnProps = {
    status: TaskFilter,
    showUser?: boolean,
    theme?: Theme,
    settings?: IDashboardSettings | null,
    setSettings?: SetPreferenceSignature,
};

type UseWorkItemTasksColumnResult = [
    GridColDef[],
    GridColumnVisibilityModel,
    ColumnVisibilityOrOrderChangeHandler,
];

type UseWorkItemTasksColumnsSignature = (props: UseWorkItemTasksColumnProps) => UseWorkItemTasksColumnResult;

const UseWorkItemTasksColumns: UseWorkItemTasksColumnsSignature = (props) => {

    const navigate = useNavigate();
    const navigateToTask = (taskId: number) => {
        navigate(`/tasks/${taskId}/overview`);
      };
    const permissions = useProjectPermissions();

    const ColumnsBase: GridColDef[] = [
        {
            field: 'name', headerName: 'Task',
            headerClassName: 'header',
            sortable: true, hideSortIcons: false,
            resizable: true,
            hideable: false,
            minWidth: 300,
            filterOperators: TextFilterOperator,
            renderCell: (params) => {
                const name = params.row.name;
                const taskId = params.row.id;
                const hasAccess = params.row.userHasAccess;
                return (
                    <div style={{ whiteSpace: 'pre-wrap', flexWrap: 'nowrap' }}>
                        {hasAccess ? (
                            <a 
                                href={`/tasks/${taskId}/overview`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigateToTask(taskId);
                                }} 
                                style={{ color: '#0066CC', cursor: 'pointer', textDecoration: 'underline' }}
                                role="link"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        navigateToTask(taskId);
                                    }
                                }}
                                aria-label={`Open task: ${name}`}
                            >
                                {name}
                            </a>
                        ) : (
                            <span>{name}</span>
                        )}
                    </div>
                );
            }
        },        
        {
            field: 'startDate', headerName: 'Start Date',
            headerClassName: 'header',
            sortable: true, hideSortIcons: false,
            resizable: true, width: 200,
            filterOperators: DateFilterOperator,
            hideable: false,
            valueGetter: (value) => !value ? null : moment.utc(value).toDate(),
            renderCell: (params) => {
                const formattedDate = formatDate(params.value);
                return (
                    <div aria-label={`Start date: ${formattedDate || 'Not set'}`}>
                        {formattedDate}
                    </div>
                );
            }
        },
        {
            field: 'endDate', headerName: 'End Date',
            headerClassName: 'header',
            sortable: true, hideSortIcons: false,
            resizable: true, width: 200,
            filterOperators: DateFilterOperator,
            hideable: false,
            valueGetter: (value) => !value ? null : moment.utc(value).toDate(),
            renderCell: (params) => {
                const formattedDate = formatDate(params.value);
                return (
                    <div aria-label={`End date: ${formattedDate || 'Not set'}`}>
                        {formattedDate}
                    </div>
                );
            }
        },
        {
            field: 'status', headerName: 'Status',
            headerClassName: 'header',
            sortable: true, hideSortIcons: false,
            resizable: true, width: 200,
            filterOperators: TaskStatusFilterOperator,
            hideable: false,
            renderCell: (params) => {
                return (
                    <TaskStatusCell 
                        status={params.value} 
                        aria-label={`Task status: ${params.value || 'Not set'}`}
                    />
                );
            }
        },
        {
            field: 'progress', headerName: 'Progress',
            headerClassName: 'header',
            width: 200, sortable: true, resizable: true,
            hideSortIcons: false,
            filterOperators: TaskProgressFilterOperator(false),
            renderCell: (params) => {
                const taskStatus = params.row.status;
                return taskStatus && String(taskStatus).toLowerCase() === 'in-progress' ? (
                    <TaskProgressPill 
                        progress={params.value}
                        aria-label={`Task progress: ${params.value || 'Not set'}`}
                    />
                ) : null;
            }
        },        
        {
            field: 'workflowName', headerName: 'Workflow',
            headerClassName: 'header',
            sortable: true, hideSortIcons: false,
            resizable: true,
            minWidth: 200,
            filterOperators: TextFilterOperator,
            renderCell: (params) => {
                const name = params.value;
                const workflowId = params.row.workflowId;
                return (
                    <Tooltip 
                        title={name} 
                        arrow 
                        describeChild 
                        enterDelay={700}
                        leaveDelay={300}
                    >
                        <div style={{ whiteSpace: 'break-spaces' }}>
                            {permissions?.canEditWorkflows ? (
                                <a 
                                    href={process.env.REACT_APP_CLASSIC_URL + `/workflowinstance/editworkflow?workflowInstanceId=${workflowId}`}
                                    style={{ color: '#0066CC', cursor: 'pointer', textDecoration: 'underline' }}
                                    rel="noopener noreferrer"
                                    role="link"
                                    tabIndex={0}
                                    aria-label={`Open workflow: ${name}`}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            window.location.href = process.env.REACT_APP_CLASSIC_URL + `/workflowinstance/editworkflow?workflowInstanceId=${workflowId}`;
                                        }
                                    }}
                                >
                                    {name}
                                </a>
                            ) : (
                                <span aria-label={`Workflow: ${name}`}>{name}</span>
                            )}
                        </div>
                    </Tooltip>
                );
            }
        },
    ];

    const { status } = props;
    const { settings, setSettings } = props;
    const localTheme = useTheme();
    const theme = props?.theme ?? localTheme;
    const showUser = props?.showUser ?? false;
    const [columns, setColumns] = useState(ColumnsBase);

    const [columnsModel, visibilityModel, handleColumnVisibilityOrOrderChange] = useGridColumns({ columns: columns, settings, setSettings })

    useEffect(() => {
        let newColumns: GridColDef[] = ColumnsBase;

        if (status !== 'in-progress' && status !== 'all') {
            const progressIdx = newColumns.findIndex(c => c.field === 'progress');
            if (progressIdx > -1) newColumns.splice(progressIdx, 1);
        }

        if (status !== 'all') {
            const statusIdx = newColumns.findIndex(c => c.field === 'status');
            if (statusIdx > -1) newColumns.splice(statusIdx, 1);
        }

        setColumns(newColumns);
    }, [props.status]);

    return [
        columnsModel,
        visibilityModel,
        handleColumnVisibilityOrOrderChange
    ];
};

export default UseWorkItemTasksColumns;